export type ColorType = 'red' | 'blue' | 'yellow' | 'purple';
export const COLORS: readonly ColorType[] = ['red', 'blue', 'yellow', 'purple'] as const;

export type SkillType = 'disrupt' | 'shield';
export const SKILLS: readonly SkillType[] = ['disrupt', 'shield'] as const;

// ── 2v2 Team mode ────────────────────────────────────────────────────
// alternating    — teammates take turns (ALT_TURN_STEPS each).
// split-color    — each teammate owns 2 colors; owner of the next hold's color taps.
// attack-defense — one teammate climbs, the other only casts skills (by lobby order).
export type TeamMode = 'alternating' | 'split-color' | 'attack-defense';
export const TEAM_MODES: readonly TeamMode[] = [
  'alternating',
  'split-color',
  'attack-defense',
] as const;
// Lane identity for a team match: 'a' = left lane, 'b' = right lane.
export type TeamSide = 'a' | 'b';
// attack-defense roles.
export type TeamRole = 'climber' | 'attacker';

export type RankTier =
  | 'bronze'
  | 'silver'
  | 'gold'
  | 'platinum'
  | 'diamond'
  | 'master'
  | 'challenger';
export const RANK_TIERS: readonly RankTier[] = [
  'bronze',
  'silver',
  'gold',
  'platinum',
  'diamond',
  'master',
  'challenger',
] as const;

/** Avatar frame render spec. `imageUrl` (a transparent PNG ring overlay) wins
 * when set; otherwise the client falls back to the CSS `border` + `glow` ring. */
export interface FrameSpec {
  border?: string | null;
  glow?: string | null;
  imageUrl?: string | null;
}

/**
 * Avatar frame per rank tier. Frames are derived from `user.rank` — there is no
 * separate equippable frame cosmetic. Ranking up/down swaps the frame for free.
 * Colors mirror the RankMedal palette so the frame matches the player's badge.
 */
// `glow` is a COLOR (the client wraps it as `0 0 14px <glow>, 0 0 4px <glow>`),
// `border` is a CSS color or gradient used as the ring background.
export const RANK_FRAMES: Record<RankTier, FrameSpec> = {
  bronze: {
    border: 'linear-gradient(135deg, #cd7f32, #8b4513)',
    glow: 'rgba(205,127,50,0.55)',
  },
  silver: {
    border: 'linear-gradient(135deg, #f4f4f4, #999)',
    glow: 'rgba(220,220,220,0.55)',
  },
  gold: {
    border: 'linear-gradient(135deg, #F4C724, #a07c00)',
    glow: 'rgba(244,199,36,0.6)',
  },
  platinum: {
    border: 'linear-gradient(135deg, #d4f5fb, #22D3EE)',
    glow: 'rgba(34,211,238,0.6)',
  },
  diamond: {
    border: 'linear-gradient(135deg, #a4d8ff, #3B82F6)',
    glow: 'rgba(59,130,246,0.65)',
  },
  master: {
    border: 'linear-gradient(135deg, #d8b4fe, #9B5DE5)',
    glow: 'rgba(155,93,229,0.75)',
  },
  challenger: {
    border:
      'conic-gradient(from 0deg, #E63946, #F4C724, #22D3EE, #9B5DE5, #E63946)',
    glow: 'rgba(244,199,36,0.9)',
  },
};

export type ClimberPose = 'idle' | 'climb' | 'fall' | 'victory';

export type MatchPhase = 'idle' | 'queuing' | 'reveal' | 'countdown' | 'racing' | 'finished';

export type MatchSide = 'left' | 'right';

export interface PlayerProfile {
  id: string;
  name: string;
  avatarColor: 'red' | 'blue';
  avatarUrl: string | null;
  /** Legacy combined MMR. Kept for back-compat reads; new UI uses mmrSolo/mmrDuo. */
  mmr: number;
  /** Per-mode MMR. Split from `mmr` so 1v1 and 2v2 ladders are independent. */
  mmrSolo: number;
  mmrDuo: number;
  /** Canonical visible rank — mirrors the SOLO (1v1) ladder. */
  rank: RankTier;
  /** Division within the tier — 4 = lowest (e.g. Bronze IV), 1 = highest. */
  rankDivision: number;
  /** Stars earned in the current division. */
  rankStars: number;
  /** Independent ranked-2v2 ladder (mirrors `rank` for solo). */
  rankDuo: RankTier;
  rankDivisionDuo: number;
  rankStarsDuo: number;
  coins: number;
  gems: number;
  xp: number;
  winStreak: number;
  streakSolo: number;
  streakDuo: number;
  matchesPlayed: number;
  matchesWon: number;
  /** Equipped avatar frame render spec — populated from `/me` on the
   * server, drives the image overlay (or colored ring + glow) around the
   * user's Avatar. */
  frame?: FrameSpec | null;
  /** Idle PNG of the equipped Character. Used as the avatar image when the
   * player hasn't uploaded a photo (`avatarUrl` is null). */
  characterIdleUrl?: string | null;
}

export interface SkillLoadout {
  slot0: SkillType | null;
  slot1: SkillType | null;
  slot2: SkillType | null;
  disruptLevel: number;
  shieldLevel: number;
}

export interface MatchSnapshot {
  matchId: string;
  you: PlayerProfile;
  rival: PlayerProfile;
  seq: { you: ColorType[]; rival: ColorType[] };
  paths: { you: number[]; rival: number[] };
  wallId: string;
  wrTime: number;
  startedAt: number;
}

export interface MatchResult {
  winnerId: string;
  finalSteps: { you: number; rival: number };
  durationMs: number;
  mmrDelta: { you: number; rival: number };
  rewards: { coins: number; xp: number };
}

export interface TapLogEntry {
  playerId: string;
  color: ColorType;
  step: number;
  t: number;
  correct: boolean;
}
